import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiMinus, FiSearch, FiHelpCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import dataService from '../services/dataService';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const data = await dataService.getFAQs();
        setFaqs(data);
      } catch (error) {
        console.error('Failed to load FAQs:', error);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    };

    loadFAQs();
  }, []);

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        pageType="faq"
        title="Frequently Asked Questions | Support Center | JIVA NADI Therapy"
        description="Find answers to common questions about chiropractic care, acupuncture treatments, and holistic healing therapies. Get comprehensive information about our services."
        keywords="FAQ, frequently asked questions, chiropractic FAQ, acupuncture questions, therapy information, treatment queries, support center"
      />

      {/* Reusable Hero Section */}
      <HeroSection
        title="Get Your General Answer"
        subtitle="Support Center"
        description="Looking for answers? Look no further! Get your general questions answered right here. Our comprehensive database covers a wide range of topics, providing you with accurate and reliable information."
        image="https://images.unsplash.com/photo-1665789318391-6057c533005e?w=800&h=600&fit=crop"
        badge="Support Center"
        primaryButton={{
          text: "Search FAQs",
          href: "#faq-search"
        }}
        secondaryButton={{
          text: "Contact Support",
          href: "/contact"
        }}
        trustIndicators={[
          { value: "100+", label: "Questions" },
          { value: "24/7", label: "Available" },
          { value: "Expert", label: "Answers" }
        ]}
      />

      {/* Search Section */}
      <section id="faq-search" className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="max-w-2xl mx-auto relative group">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your health concern (e.g., Chiropractic safety, Nadi Pariksha)..."
              className="w-full bg-white border border-gray-200 rounded-2xl px-12 py-4 text-gray-900 placeholder-gray-400 focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-lg shadow-sm"
            />
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500 w-5 h-5" />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Grid */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          {filteredFaqs.length > 0 ? (
            <div className="space-y-6">
              {filteredFaqs.map((faq, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`rounded-[2.5rem] transition-all duration-500 shadow-xl shadow-emerald-900/5 ${openIndex === index ? 'bg-white ring-2 ring-emerald-500' : 'bg-white border border-gray-100 hover:border-emerald-200'}`}
                >
                  <button 
                    onClick={() => setOpenIndex(openIndex === index ? -1 : index)} 
                    className="w-full px-10 py-10 text-left flex justify-between items-center group"
                  >
                    <h3 className={`text-xl lg:text-2xl font-black transition-colors ${openIndex === index ? 'text-emerald-600' : 'text-gray-800 group-hover:text-emerald-600'}`}>
                      {faq.question}
                    </h3>
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 flex-shrink-0 ml-6 ${openIndex === index ? 'bg-emerald-600 text-white rotate-180' : 'bg-emerald-50 text-emerald-600'}`}>
                      <SafeIcon icon={openIndex === index ? FiMinus : FiPlus} className="w-6 h-6" />
                    </div>
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div 
                        initial={{ height: 0, opacity: 0 }} 
                        animate={{ height: 'auto', opacity: 1 }} 
                        exit={{ height: 0, opacity: 0 }} 
                        className="px-10 pb-10"
                      >
                        <div className="pt-8 border-t border-emerald-50 text-gray-600 text-lg leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-gray-300">
              <SafeIcon icon={FiHelpCircle} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-xl font-medium">No answers found for "{searchQuery}"</p>
              <button onClick={() => setSearchQuery('')} className="mt-4 text-emerald-600 font-bold hover:underline">Show all questions</button>
            </div>
          )}
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-emerald-900 rounded-[3rem] p-12 lg:p-20 text-center text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-800 rounded-full -mr-32 -mt-32 blur-3xl opacity-50"></div>
            <div className="relative z-10">
              <h2 className="text-3xl lg:text-4xl font-black mb-6">Didn't find what you were looking for?</h2>
              <p className="text-emerald-100/70 text-lg mb-10 max-w-2xl mx-auto">
                Our medical coordinators are available 10:00 AM - 09:00 PM to assist you with specific medical queries regarding your pain.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9581556660" className="bg-white text-emerald-900 px-10 py-4 rounded-full font-black hover:bg-emerald-50 transition-all shadow-xl">
                  Call: 9581556660
                </a>
                <a href="https://wa.me/919581556660" className="bg-emerald-500 text-white px-10 py-4 rounded-full font-black hover:bg-emerald-400 transition-all border border-emerald-400">
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;